import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getAllVacationPlans,
    getVacationCalendar,
    reviewVacationPlan,
    getUsers,
    getRedDays
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Check, X, Clock, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Users, Filter } from 'lucide-react';

export default function VacationCalendarPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [allPlans, setAllPlans] = useState([]);
    const [pendingPlans, setPendingPlans] = useState([]);
    const [historyPlans, setHistoryPlans] = useState([]);
    const [users, setUsers] = useState([]);
    const [redDays, setRedDays] = useState([]);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [reviewData, setReviewData] = useState({ status: '', comment: '' });
    const [processing, setProcessing] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    // View settings
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [viewRange, setViewRange] = useState('month'); // 'month' or 'quarter'
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'approved', 'pending'

    useEffect(() => {
        fetchData();
    }, [currentYear, currentMonth]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Calculate date range based on view
            let startDate, endDate;
            if (viewRange === 'quarter') {
                const quarterStart = Math.floor(currentMonth / 3) * 3;
                startDate = `${currentYear}-${String(quarterStart + 1).padStart(2, '0')}-01`;
                const quarterEnd = quarterStart + 2;
                const lastDay = new Date(currentYear, quarterEnd + 1, 0).getDate();
                endDate = `${currentYear}-${String(quarterEnd + 1).padStart(2, '0')}-${lastDay}`;
            } else {
                startDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
                const lastDay = new Date(currentYear, currentMonth + 1, 0).getDate();
                endDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${lastDay}`;
            }

            const [plansRes, pendingRes, usersRes, redDaysRes] = await Promise.all([
                getVacationCalendar(startDate, endDate),
                getAllVacationPlans('pending'),
                getUsers(),
                getRedDays(currentYear)
            ]);

            // Also get pending plans to show in Gantt
            const allPendingRes = await getAllVacationPlans();
            const historyRes = await getAllVacationPlans('approved');

            const activeUsers = usersRes.data || [];
            const activeUserIds = new Set(activeUsers.map(u => u.id));
            const calendarVacations = (plansRes.data.vacations || []).filter(p => activeUserIds.has(p.user_id));
            const pendingFromAll = (allPendingRes.data || [])
                .filter(p => p.status === 'pending')
                .filter(p => activeUserIds.has(p.user_id));

            const pendingOnly = (pendingRes.data || []).filter(p => activeUserIds.has(p.user_id));
            const approvedPlans = (historyRes.data || []).filter(p => activeUserIds.has(p.user_id));

            setUsers(activeUsers);
            setAllPlans([...calendarVacations, ...pendingFromAll]);
            setPendingPlans(pendingOnly);
            setHistoryPlans(approvedPlans);

            setRedDays(redDaysRes.data?.red_days || []);
        } catch (error) {
            console.error('Failed to load vacation data:', error);
            toast.error(t('vacation_calendar.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleReview = async () => {
        if (!selectedPlan || !reviewData.status) return;

        setProcessing(true);
        try {
            await reviewVacationPlan(selectedPlan.id, {
                status: reviewData.status,
                comment: reviewData.comment
            });
            toast.success(t('vacation.vacation_result', { status: reviewData.status }));
            setReviewDialogOpen(false);
            setSelectedPlan(null);
            setReviewData({ status: '', comment: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('vacation_calendar.failed_review'));
        } finally {
            setProcessing(false);
        }
    };

    const openReviewDialog = (plan, status) => {
        setSelectedPlan(plan);
        setReviewData({ status, comment: '' });
        setReviewDialogOpen(true);
    };

    // Get Swedish week number - must be defined before useMemo
    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    // Generate days for the timeline
    const timelineDays = useMemo(() => {
        const days = [];
        let startDate, endDate;

        if (viewRange === 'quarter') {
            const quarterStart = Math.floor(currentMonth / 3) * 3;
            startDate = new Date(currentYear, quarterStart, 1);
            endDate = new Date(currentYear, quarterStart + 3, 0);
        } else {
            startDate = new Date(currentYear, currentMonth, 1);
            endDate = new Date(currentYear, currentMonth + 1, 0);
        }

        let current = new Date(startDate);
        while (current <= endDate) {
            const dateStr = current.toISOString().split('T')[0];
            days.push({
                date: dateStr,
                day: current.getDate(),
                dayOfWeek: current.getDay(),
                isWeekend: current.getDay() === 0 || current.getDay() === 6,
                isRedDay: redDays.includes(dateStr),
                month: current.getMonth(),
                weekNumber: getWeekNumber(current)
            });
            current.setDate(current.getDate() + 1);
        }
        return days;
    }, [currentYear, currentMonth, viewRange, redDays]);

    // Group days by week for header
    const weekGroups = useMemo(() => {
        const groups = [];
        let currentWeek = null;
        let weekDays = [];

        timelineDays.forEach(day => {
            if (currentWeek !== day.weekNumber) {
                if (weekDays.length > 0) {
                    groups.push({ week: currentWeek, days: weekDays });
                }
                currentWeek = day.weekNumber;
                weekDays = [day];
            } else {
                weekDays.push(day);
            }
        });
        if (weekDays.length > 0) {
            groups.push({ week: currentWeek, days: weekDays });
        }
        return groups;
    }, [timelineDays]);

    const activeUserIds = new Set(users.map(u => u.id));

    // Get unique users with vacation data
    const usersWithVacation = useMemo(() => {
        const userMap = new Map();

        // build active id set from current users list
        const activeUserIds = new Set(users.map(u => u.id));

        // Add all active users first
        users.forEach(user => {
            userMap.set(user.id, {
                ...user,
                vacations: [],
                totalDays: 0
            });
        });

        // Add vacations to users (extra safety: skip unknown/inactive)
        allPlans.forEach(plan => {
            if (!activeUserIds.has(plan.user_id)) return;

            const user = userMap.get(plan.user_id);
            if (!user) return;

            if (statusFilter === 'all' || plan.status === statusFilter) {
                user.vacations.push(plan);
                user.totalDays += plan.days_count || 0;
            }
        });

        // Show all active employees, even if they have 0 days
        return Array.from(userMap.values()).sort((a, b) => a.name?.localeCompare(b.name));
    }, [users, allPlans, statusFilter]);


    // Calculate vacation bar position and width
    const getVacationBarStyle = (vacation) => {
        const startDate = new Date(vacation.start_date);
        const endDate = new Date(vacation.end_date);

        const firstDay = timelineDays[0];
        const lastDay = timelineDays[timelineDays.length - 1];
        const firstDate = new Date(firstDay?.date);
        const lastDate = new Date(lastDay?.date);

        // Clamp to visible range
        const visibleStart = startDate < firstDate ? firstDate : startDate;
        const visibleEnd = endDate > lastDate ? lastDate : endDate;

        // Find indices
        const startIdx = timelineDays.findIndex(d => d.date === visibleStart.toISOString().split('T')[0]);
        const endIdx = timelineDays.findIndex(d => d.date === visibleEnd.toISOString().split('T')[0]);

        if (startIdx === -1 || endIdx === -1) return null;

        const dayWidth = 100 / timelineDays.length;
        const left = startIdx * dayWidth;
        const width = (endIdx - startIdx + 1) * dayWidth;

        return { left: `${left}%`, width: `${width}%` };
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const navigatePeriod = (direction) => {
        if (viewRange === 'quarter') {
            const newMonth = currentMonth + (direction * 3);
            if (newMonth < 0) {
                setCurrentMonth(9);
                setCurrentYear(currentYear - 1);
            } else if (newMonth > 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(newMonth);
            }
        } else {
            const newMonth = currentMonth + direction;
            if (newMonth < 0) {
                setCurrentMonth(11);
                setCurrentYear(currentYear - 1);
            } else if (newMonth > 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(newMonth);
            }
        }
    };

    const getPeriodLabel = () => {
        if (viewRange === 'quarter') {
            const quarterStart = Math.floor(currentMonth / 3) * 3;
            return `Q${Math.floor(currentMonth / 3) + 1} ${currentYear} (${monthNames[quarterStart]} - ${monthNames[quarterStart + 2]})`;
        }
        return `${monthNames[currentMonth]} ${currentYear}`;
    };

    const getReviewDialogTitle = () => {
        let action;
        if (reviewData.status === 'approved') action = t('vacation_calendar.review_approve');
        else if (reviewData.status === 'rejected') action = t('vacation_calendar.review_reject');
        else action = t('vacation_calendar.review_title');
        return t('vacation_calendar.review_dialog_title', { action });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin rounded-full"></div>
            </div>
        );
    }

    const totalEmployees = usersWithVacation.length;
    const employeesWithVacation = usersWithVacation.filter(u => u.totalDays > 0).length;

    return (
        <div className="space-y-6" data-testid="vacation-calendar-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">{t('vacation_calendar.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('vacation_calendar.subtitle')}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('vacation_calendar.stat_pending')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{pendingPlans.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('vacation_calendar.stat_on_vacation')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{employeesWithVacation}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('vacation_calendar.stat_total_days')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{usersWithVacation.reduce((sum, u) => sum + u.totalDays, 0)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Mobile: show all plans as a list */}
            <div className="sm:hidden space-y-3">
                    <p className="text-sm text-muted-foreground">{getPeriodLabel()}</p>
                    {usersWithVacation.filter(u => u.totalDays > 0).length === 0 ? (
                        <Card className="border border-border shadow-none">
                            <CardContent className="p-8 text-center text-muted-foreground">
                                <p>No vacation plans this period.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        usersWithVacation.filter(u => u.totalDays > 0).map(u => (
                            <Card key={u.id} className="border border-border shadow-none">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="font-medium">{u.name}</p>
                                        <span className="text-sm font-mono text-muted-foreground">{u.totalDays}d total</span>
                                    </div>
                                    <div className="space-y-1">
                                        {u.vacations.map(plan => (
                                            <div key={plan.id} className="flex items-center justify-between text-sm">
                                                <span className="text-muted-foreground">
                                                    {plan.start_date === plan.end_date ? plan.start_date : `${plan.start_date} — ${plan.end_date}`}
                                                </span>
                                                <span className={`px-2 py-0.5 text-xs rounded text-white ${
                                                    plan.status === 'approved' ? 'bg-green-500' : 'bg-amber-500'
                                                }`}>
                                                    {plan.status}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>

            {/* Gantt Chart View */}
            <div className="hidden sm:block">
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            {/* Period Navigation */}
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => navigatePeriod(-1)}>
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="font-medium min-w-[200px] text-center">{getPeriodLabel()}</span>
                                <Button variant="outline" size="sm" onClick={() => navigatePeriod(1)}>
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setCurrentYear(new Date().getFullYear());
                                        setCurrentMonth(new Date().getMonth());
                                    }}
                                >
                                    {t('vacation_calendar.today')}
                                </Button>
                            </div>

                            {/* Filters */}
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs uppercase text-muted-foreground">{t('vacation_calendar.view_label')}</Label>
                                    <select
                                        value={viewRange}
                                        onChange={(e) => setViewRange(e.target.value)}
                                        className="h-8 px-2 border border-input bg-background text-sm rounded-md"
                                    >
                                        <option value="month">{t('vacation_calendar.view_month')}</option>
                                        <option value="quarter">{t('vacation_calendar.view_quarter')}</option>
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs uppercase text-muted-foreground">{t('vacation_calendar.status_label')}</Label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="h-8 px-2 border border-input bg-background text-sm rounded-md"
                                    >
                                        <option value="all">{t('vacation_calendar.status_all')}</option>
                                        <option value="approved">{t('vacation_calendar.status_approved')}</option>
                                        <option value="pending">{t('vacation_calendar.status_pending')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {/* Legend */}
                        <div className="flex items-center gap-6 px-4 py-2 border-b bg-muted/30 text-xs">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-green-500 rounded"></div>
                                <span>{t('vacation_calendar.legend_approved')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-blue-500 rounded"></div>
                                <span>{t('vacation_calendar.legend_pending')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-red-200 rounded"></div>
                                <span>{t('vacation_calendar.legend_red_day')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-3 bg-muted rounded"></div>
                                <span>{t('vacation_calendar.legend_weekend')}</span>
                            </div>
                        </div>

                        {/* Gantt Chart */}
                        <div className="overflow-x-auto">
                            <div className="min-w-[800px]">
                                {/* Week Headers */}
                                <div className="flex border-b">
                                    <div className="w-48 flex-shrink-0 p-2 bg-muted/50 border-r font-medium text-sm">
                                        {t('vacation_calendar.employee_col')}
                                    </div>
                                    <div className="flex-1 flex">
                                        {weekGroups.map((group, idx) => (
                                            <div
                                                key={idx}
                                                className="border-r bg-muted/30 text-center text-xs font-medium py-1"
                                                style={{ width: `${(group.days.length / timelineDays.length) * 100}%` }}
                                            >
                                                {t('vacation_calendar.week_label', { number: group.week })}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Day Headers */}
                                <div className="flex border-b">
                                    <div className="w-48 flex-shrink-0 p-2 bg-muted/50 border-r text-xs text-muted-foreground">
                                        {t('vacation_calendar.total_days')}
                                    </div>
                                    <div className="flex-1 flex">
                                        {timelineDays.map((day, idx) => (
                                            <div
                                                key={idx}
                                                className={`flex-1 text-center text-[10px] py-1 border-r last:border-r-0 ${
                                                    day.isRedDay ? 'bg-red-100 text-red-700' :
                                                    day.isWeekend ? 'bg-muted/50 text-muted-foreground' : ''
                                                }`}
                                                title={day.date}
                                            >
                                                <div className="font-medium">{day.day}</div>
                                                <div className="text-[9px]">{dayNames[day.dayOfWeek]}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* User Rows */}
                                {usersWithVacation.length === 0 ? (
                                    <div className="flex items-center justify-center py-12 text-muted-foreground">
                                        <div className="text-center">
                                            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>{t('vacation_calendar.no_vacation_plans')}</p>
                                            <p className="text-sm">{t('vacation_calendar.adjust_filters')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    usersWithVacation.map(user => (
                                        <div key={user.id} className="flex border-b hover:bg-muted/20">
                                            {/* User Name */}
                                            <div className="w-48 flex-shrink-0 p-2 border-r flex items-center gap-2">
                                                <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                                                    {user.name?.charAt(0) || '?'}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-sm truncate">{user.name}</p>
                                                    <p className="text-xs text-muted-foreground">({user.totalDays}d)</p>
                                                </div>
                                            </div>

                                            {/* Timeline */}
                                            <div className="flex-1 relative h-12">
                                                {/* Day backgrounds */}
                                                <div className="absolute inset-0 flex">
                                                    {timelineDays.map((day, idx) => (
                                                        <div
                                                            key={idx}
                                                            className={`flex-1 border-r last:border-r-0 ${
                                                                day.isRedDay ? 'bg-red-50' :
                                                                day.isWeekend ? 'bg-muted/30' : ''
                                                            }`}
                                                        />
                                                    ))}
                                                </div>

                                                {/* Vacation bars */}
                                                <div className="absolute inset-0 py-2 px-0.5">
                                                    {user.vacations.map(vacation => {
                                                        const style = getVacationBarStyle(vacation);
                                                        if (!style) return null;

                                                        return (
                                                            <div
                                                                key={vacation.id}
                                                                className={`absolute h-6 rounded cursor-pointer transition-opacity hover:opacity-80 flex items-center justify-center text-white text-[10px] font-medium shadow-sm ${
                                                                    vacation.status === 'approved' ? 'bg-green-500' : 'bg-blue-500'
                                                                }`}
                                                                style={{ ...style, top: '50%', transform: 'translateY(-50%)' }}
                                                                onClick={() => vacation.status === 'pending' && openReviewDialog(vacation, '')}
                                                                title={`${vacation.start_date} - ${vacation.end_date} (${vacation.days_count}d) - ${vacation.status}`}
                                                            >
                                                                {vacation.days_count}d
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Summary */}
                        {usersWithVacation.length > 0 && (
                            <div className="p-4 bg-muted/30 border-t">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {t('vacation_calendar.summary_employees', { count: employeesWithVacation, total: totalEmployees })}
                                    </span>
                                    <span className="font-medium">
                                        {t('vacation_calendar.summary_total', { days: usersWithVacation.reduce((sum, u) => sum + u.totalDays, 0) })}
                                    </span>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
                </div>

            {/* Pending Requests — always visible below calendar */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    <div className="px-4 py-3 border-b border-border">
                        <h2 className="font-heading text-lg font-bold flex items-center gap-2">
                            <Clock className="w-5 h-5 text-amber-600" />
                            {t('vacation_calendar.pending_title', { count: pendingPlans.length })}
                        </h2>
                    </div>
                    {pendingPlans.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Check className="w-10 h-10 mx-auto mb-2 opacity-50 text-green-500" />
                            <p>{t('vacation_calendar.all_caught_up')}</p>
                            <p className="text-sm">{t('vacation_calendar.no_pending')}</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {pendingPlans.map(plan => (
                                <div key={plan.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 hover:bg-muted/30 transition-colors gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0">
                                            {plan.user_name?.charAt(0) || '?'}
                                        </div>
                                        <div>
                                            <p className="font-medium">{plan.user_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(plan.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                {plan.start_date !== plan.end_date && (
                                                    <> — {new Date(plan.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between sm:justify-end gap-4">
                                        <p className="font-bold">{t('vacation_calendar.days_count', { count: plan.days_count })}</p>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => openReviewDialog(plan, 'approved')}>
                                                <Check className="w-4 h-4" />
                                            </Button>
                                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => openReviewDialog(plan, 'rejected')}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* History (approved/reviewed) */}
            {historyPlans.length > 0 && (
                <Card className="border border-border shadow-none">
                    <button
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                        onClick={() => setShowHistory(v => !v)}
                    >
                        <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                            {t('vacation_calendar.history_label')} ({historyPlans.length})
                        </span>
                        {showHistory ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {showHistory && (
                        <CardContent className="p-0 border-t border-border">
                            <div className="divide-y divide-border">
                                {historyPlans.map(plan => (
                                    <div key={plan.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 gap-3 opacity-75">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0">
                                                {plan.user_name?.charAt(0) || '?'}
                                            </div>
                                            <div>
                                                <p className="font-medium">{plan.user_name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {new Date(plan.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                    {plan.start_date !== plan.end_date && (
                                                        <> — {new Date(plan.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <p className="font-mono text-sm">{plan.days_count}d</p>
                                            <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                                                plan.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {plan.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}

            {/* Review Dialog */}
            <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {getReviewDialogTitle()}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedPlan && (
                        <div className="space-y-4 py-4">
                            <div className="p-4 bg-muted rounded-lg">
                                <p className="font-medium">{selectedPlan.user_name}</p>
                                <p className="text-sm text-muted-foreground">
                                    {new Date(selectedPlan.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    {selectedPlan.start_date !== selectedPlan.end_date && (
                                        <> — {new Date(selectedPlan.end_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</>
                                    )}
                                </p>
                                <p className="text-lg font-bold mt-1">{selectedPlan.days_count} {t('vacation_calendar.working_days')}</p>
                                {selectedPlan.notes && (
                                    <p className="text-sm mt-2 italic">{selectedPlan.notes}</p>
                                )}
                            </div>

                            {!reviewData.status && (
                                <div className="flex gap-2">
                                    <Button
                                        className="flex-1 bg-green-600 hover:bg-green-700"
                                        onClick={() => setReviewData(prev => ({ ...prev, status: 'approved' }))}
                                    >
                                        <Check className="w-4 h-4 mr-2" />
                                        {t('vacation_calendar.approve_btn')}
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        className="flex-1"
                                        onClick={() => setReviewData(prev => ({ ...prev, status: 'rejected' }))}
                                    >
                                        <X className="w-4 h-4 mr-2" />
                                        {t('vacation_calendar.reject_btn')}
                                    </Button>
                                </div>
                            )}

                            {reviewData.status && (
                                <div>
                                    <Label>{t('vacation_calendar.comment_label')}</Label>
                                    <Textarea
                                        value={reviewData.comment}
                                        onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
                                        placeholder={reviewData.status === 'rejected'
                                            ? t('vacation_calendar.rejection_placeholder')
                                            : t('vacation_calendar.comments_placeholder')}
                                        rows={2}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setReviewDialogOpen(false);
                            setReviewData({ status: '', comment: '' });
                        }}>
                            {t('common.cancel')}
                        </Button>
                        {reviewData.status && (
                            <Button
                                onClick={handleReview}
                                disabled={processing}
                                className={reviewData.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                                variant={reviewData.status === 'rejected' ? 'destructive' : 'default'}
                            >
                                {processing
                                    ? t('vacation_calendar.processing')
                                    : reviewData.status === 'approved'
                                        ? t('vacation_calendar.confirm_approval')
                                        : t('vacation_calendar.confirm_rejection')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
